import React, { memo, useEffect, useState } from "react";

import { ConfigProvider, Dropdown, Form, Input, Select, Upload } from "antd";

import TextArea from "antd/es/input/TextArea";
import classNames from "classnames";

import ArrowIconWhite from "@assets/icons/ArrowIconWhite";
import StopGenerationIcon from "@assets/icons/StopGenerationIcon";
import Copied from "@assets/icons/copied.svg";
import CopyIcon from "@assets/playground/icon_copy.svg";
import Close from "@assets/icons/cross.svg";
import Close2 from "@assets/icons/cross2.svg";
import down from "@assets/images/down.svg";
import Spinner from "@assets/icons/spinner.svg";
import UploadImage from "@assets/playground/icon_upload_image.svg";
import UploadDocument from "@assets/playground/icon_upload_doc.svg";
import ChatAIOutputIcon from "@assets/icons/chat_ai_output.png";
import IconAdd from "@assets/playground/icon_add.svg";
import { ReactComponent as WarningIcon } from "@assets/icons/warning.svg";
import IconLink from "@assets/playground/icon_link.svg";
import IconInfo from "@assets/playground/icon_info.svg";
import { ChatRole, IChat } from "@components/pages/project/Playground/types";
import MarkdownViewer, {
  processMarkdown,
} from "@components/common-components/markdown-viewer";
import { useStore } from "@stores/root-store";

import CNEvaluator, {
  CN_EVALUATOR_PLUGIN_SECTIONS,
  CNEvaluatorTooltip1,
  CNEvaluatorTooltip2,
  CNEvaluatorTooltip3,
} from "./plugins/CNEvaluator";

import { validateMessages } from "@utils/json-data";

import pdfButtonIcon from "../../../../assets/icons/writing-tips-button.svg";
import style from "./style.module.scss";
import testSvg from "@assets/icons/test.svg";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import { allCountries } from "@utils/helpers/allCountries";
import { useOutsideClick } from "@utils/hooks/useClickOutside";
import { readFileAsArrayBuffer } from "@utils/common-functions";
import { vercelEnv } from "@api/const";
import { FileV2 } from "../../../../../../types/common-interfaces";
import { SubmissionStage } from "@components/pages/project/single-functions/cn-converter/types";
import { useAutoScroll } from "@utils/hooks/useAutoScroll";

interface AdvisorFormProps {
  initialSystemMessage: JSX.Element;
  submissionStage: string;
  required: boolean;
  disabled: boolean;
  inputPlaceholder: string;
  isContinueMode?: boolean;
  continueChatText?: string;
  handleSubmit: (data?: any) => void;
  handleRegenerate: () => void;
  setInput?: (val: string) => void;
  chat: IChat[];
  url?: string;
  setUrl: (val: string) => void;
  isUrlBoxOpen: boolean;
  setIsUrlBoxOpen: (val: boolean) => void;
  form: any;
  selectedImage?: File | null;
  setSelectedImage?: (file: File) => void;
  selectedDocuments?: FileV2[] | null;
  setSelectedDocuments?: (files: FileV2[]) => void;
  isSubmitting?: boolean;
  input?: string;
  stopGeneration?: () => void;
  errorMessage: string;
  plugins: { CNEvaluator: boolean };
  pluginHandlers: any;
}

const PlaygroundForm: React.FC<AdvisorFormProps> = ({
  initialSystemMessage,
  submissionStage,
  disabled,
  required,
  inputPlaceholder,
  isContinueMode,
  handleSubmit,
  handleRegenerate,
  form,
  isSubmitting,
  input,
  selectedImage,
  setSelectedImage,
  setInput,
  chat,
  continueChatText,
  url,
  setUrl,
  isUrlBoxOpen,
  setIsUrlBoxOpen,
  errorMessage,
  stopGeneration,
  selectedDocuments,
  setSelectedDocuments,
  plugins,
  pluginHandlers,
}) => {
  const {
    user: { getUserType },
    modelType: { changeModelType, getSelectedModelType },
  } = useStore(null);
  const [copiedIndex, setCopiedIndex] = React.useState<number>(-1);
  const [preview, setPreview] = useState("");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const addMenuRef = useOutsideClick(() => setIsAddMenuOpen(false));

  const userType: string = getUserType;

  const onClickEnter = (event) => {
    if (event.key === "Enter" && input.trim().length) {
      event.preventDefault();
      handleSubmit("");
    }
  };

  const [openModal, setOpenModal] = useState(false);

  const displayedChat = [...chat];

  // If isSubmitting is true, append a virtual system message
  if (isSubmitting && submissionStage !== SubmissionStage.Idle) {
    displayedChat.push({
      id: Date.now() + Math.floor(Math.random() * 1000),
      role: ChatRole.CHAT,
      message: "",
      isLastChatMessage: true,
    });
  }

  // If there is an error, append a virtual system message
  if (errorMessage.trim() !== "") {
    displayedChat.push({
      id: 0,
      role: ChatRole.CHAT,
      message: errorMessage,
      isLastChatMessage: false,
      url: "",
      containsFile: false,
      isError: true,
      image: null,
      files: [],
    });
  }

  // TODO: enable when needed [getting started modal]
  // const [openModal, setOpenModal] = useState(
  //   localStorage.getItem("showPlaygroundTooltip") || "true",
  // );

  useEffect(() => {
    form.setFieldValue("q", input);
  }, [input]);

  const chatRef = useAutoScroll(displayedChat);

  useEffect(() => {
    switch (userType) {
      case "basic":
        changeModelType("Titan Pro");
        return;
      case "premium":
        changeModelType("Titan Pro");
        return;
      case "enterprise":
        changeModelType("Titan Pro");
        return;
      default:
        changeModelType("Titan Pro");
        return;
    }
  }, [getUserType]);

  useEffect(() => {
    if (!copiedIndex) return;

    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  }, [copiedIndex]);

  useEffect(() => {
    if (!selectedImage) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedImage);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedImage]);

  const systemIcon = (
    <div className={style.chatMessageSystemAIIcon}>
      <img src={ChatAIOutputIcon} alt="AI" />
    </div>
  );

  const handleFileChange = async (info) => {
    const files = info.fileList.map((file) => file.originFileObj);

    const fileDataArray = await Promise.all(
      files.map(async (file) => {
        const id = Date.now() + Math.floor(Math.random() * 1000);
        const buffer = await readFileAsArrayBuffer(file);
        return { id, filename: file.name, data: buffer };
      }),
    );

    setSelectedDocuments(fileDataArray);
    setIsAddMenuOpen(false);
  };

  const getModelOptions = (userType: string, getSelectedModelType: string) => {
    const baseOptions = {
      Research: {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Research" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Perplexity Deep Research
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Use as a stand-alone tool to perform climate research. It reads
              hundreds of sources, and reasons through the material to
              autonomously deliver a comprehensive report.
            </div>
          </div>
        ),
        value: "Research",
        miniLabel: "Research",
      },
      Custom: {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Custom" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Custom Model (Llama 4)
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              An in-house language model for secure use, trained on donor
              guidance, internal policies, and your selected information.
            </div>
          </div>
        ),
        value: "Custom",
        miniLabel: "Custom (Llama 4)",
        disabled: true, // TODO: enable when needed
      },
      "Titan Pro": {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Titan Pro" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Titan Pro
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Smartest language model available. Advanced reasoning. Responses
              may take longer than average.
            </div>
          </div>
        ),
        value: "Titan Pro",
        miniLabel: "Titan Pro",
      },
      "Titan Flash": {
        label: (
          <div className={style.modelSelectorDropdownLabel}>
            <div className={style.modelSelectorDropdownLabelTitle}>
              {getSelectedModelType === "Titan Flash" && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "5px",
                  }}
                />
              )}
              Titan Flash
            </div>
            <div className={style.modelSelectorDropdownLabelDesc}>
              Our powerful default model with fast responses. Great for
              reviewing resources and general queries.
            </div>
          </div>
        ),
        value: "Titan Flash",
        miniLabel: "Titan Flash",
      },
      close: {
        label: (
          <span
            style={{
              display: "flex",
              height: "100%",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <DownOutlined />
          </span>
        ),
        value: "close",
      },
    };

    const typeMappings: Record<string, string[]> = {
      basic: ["Titan Pro", "Titan Flash", "Custom"],
      premium: ["Titan Pro", "Titan Flash", "Custom"],
      enterprise: ["Titan Pro", "Titan Flash", "Custom"],
    };

    return (typeMappings[userType] || [])
      .map((model) => baseOptions[model])
      .concat(baseOptions.close);
  };

  const getSubmissionStageMessage = () => {
    const stage = submissionStage;

    switch (stage) {
      case SubmissionStage.Idle:
        return "";
      case SubmissionStage.Uploading:
        return "Uploading Concept Note...";
      case SubmissionStage.Analyzing:
        return "Reviewing your Concept Note for pertinent information...";
      case SubmissionStage.Preparing:
        return "Reviewing GCF guidelines and criteria...";
      case SubmissionStage.Generating:
        return "Preparing the output...";
      case SubmissionStage.Complete:
        return "Concept Note generation complete";
      case SubmissionStage.Error:
        return "An error occurred during the Concept Note generation";
      default:
        return "";
    }
  };

  return (
    <>
      <div className={style.formWrapper + " playgroundns"}>
        <Form
          className={style.form}
          form={form}
          autoComplete="false"
          onFinish={handleSubmit}
          validateMessages={validateMessages}
          layout="vertical"
          initialValues={{
            q: input,
          }}
        >
          <div
            className={style.wrapper}
            style={{
              maxHeight: selectedImage ? "60vh" : "70vh",
            }}
          >
            <div
              className={style.chat}
              style={
                plugins.CNEvaluator ? { height: "calc(100vh - 345px)" } : {}
              }
              id="chat"
              ref={chatRef}
            >
              {isContinueMode !== true && !continueChatText ? (
                <div
                  key="initialMessage"
                  className={[style.chatMessage, style.chatMessageSystem].join(
                    " ",
                  )}
                >
                  {systemIcon}{" "}
                  <div className={style.chatMessageText}>
                    <p>{initialSystemMessage}</p>
                  </div>
                </div>
              ) : (
                <div
                  key="initialMessage"
                  className={[style.chatMessage, style.chatMessageSystem].join(
                    " ",
                  )}
                >
                  {systemIcon}{" "}
                  <div className={style.chatMessageText}>
                    <MarkdownViewer text={continueChatText} type="playground" />
                  </div>
                </div>
              )}
              {!!displayedChat.length
                ? displayedChat.map((el: IChat, index: number) => {
                    return (
                      <div
                        key={index}
                        className={[
                          style.chatMessage,
                          el.isError === true ? style.chatMessageError : null,
                          el.role === ChatRole.CHAT
                            ? style.chatMessageSystem
                            : style.chatMessageUser,
                        ].join(" ")}
                      >
                        {el.role === ChatRole.CHAT ? systemIcon : null}
                        <div className={style.chatMessageText}>
                          {el.role === ChatRole.USER ? (
                            <>
                              {el.message}
                              {el.url !== "" ? (
                                <>
                                  <br />
                                  <br />
                                  <span>
                                    Link:{" "}
                                    <a
                                      style={{
                                        textDecoration: "underline",
                                        color: "#333",
                                      }}
                                      target="_blank"
                                      href={el.url}
                                    >
                                      {el.url}
                                    </a>
                                  </span>
                                </>
                              ) : (
                                ""
                              )}
                            </>
                          ) : el.isLastChatMessage === true &&
                            el.message === "" ? (
                            <div className={style.systemGeneratingResponse}>
                              <img src={Spinner} />
                              {getSubmissionStageMessage()}
                            </div>
                          ) : el.message === "__INTERNAL_STOPPED__" ? (
                            <div className={style.systemGeneratingResponse}>
                              Stopped
                            </div>
                          ) : el.isError === true ? (
                            <div className={style.chatMessageErrorText}>
                              <WarningIcon
                                className={style.chatMessageErrorTextIcon}
                              />
                              {el.message}
                            </div>
                          ) : (
                            <MarkdownViewer
                              text={el.message}
                              type="playground"
                            />
                          )}
                        </div>
                        {el.image && (
                          <img src={URL.createObjectURL(el.image)} alt="" />
                        )}
                        {el.files?.length > 0 && (
                          <div className={style.chatMessageDocument}>
                            Selected File{el.files?.length > 1 ? "s" : ""}:{" "}
                            <b>{el.files.join(", ")}</b>
                          </div>
                        )}

                        {el.role === ChatRole.USER && el.section !== "" ? (
                          <>
                            <span>
                              Section:{" "}
                              <span style={{ color: "#2E2E2E" }}>
                                {plugins.CNEvaluator
                                  ? CN_EVALUATOR_PLUGIN_SECTIONS.getLabel(
                                      el.section,
                                    )
                                  : ""}
                              </span>
                              {el.subsection !== "" ? (
                                <>
                                  ; Subsection:{" "}
                                  <span style={{ color: "#2E2E2E" }}>
                                    {plugins.CNEvaluator
                                      ? CN_EVALUATOR_PLUGIN_SECTIONS.getLabel(
                                          el.subsection,
                                        )
                                      : ""}
                                  </span>
                                </>
                              ) : null}
                            </span>
                          </>
                        ) : null}
                        <div className={style.chatMessageControls}>
                          <div
                            className={style.copyIcon}
                            onClick={async () => {
                              const { htmlContent, plainText } =
                                await processMarkdown(el.message);
                              // Copy both HTML and plain text to the clipboard
                              await navigator.clipboard.write([
                                new ClipboardItem({
                                  "text/html": new Blob([htmlContent], {
                                    type: "text/html",
                                  }),
                                  "text/plain": new Blob([plainText], {
                                    type: "text/plain",
                                  }),
                                }),
                              ]);

                              setCopiedIndex(index);
                            }}
                          >
                            {el.role === ChatRole.CHAT &&
                              el.message &&
                              (copiedIndex === index ? (
                                <img
                                  style={{ width: "24px", height: "24px" }}
                                  src={Copied}
                                  alt="copied"
                                />
                              ) : (
                                <img
                                  style={{ cursor: "pointer" }}
                                  src={CopyIcon}
                                  alt="copy"
                                />
                              ))}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : null}
            </div>
            <div className={style.bottomSection}>
              {plugins.CNEvaluator === true ? <CNEvaluatorTooltip1 /> : null}
              {plugins.CNEvaluator === true ? <CNEvaluatorTooltip2 /> : null}
              <Select
                // defaultValue={userType === "basic" ? "Swift" : "Titan Pro"}
                defaultValue={"Titan Pro"}
                style={{ width: "250px", textAlign: "start" }}
                listHeight={420}
                popupClassName="playgroundns"
                className={style.modelSelectorDropdown}
                optionLabelProp="miniLabel"
                suffixIcon={<img src={down} alt="down icon" />}
                onChange={(value) => {
                  if (value === "close") {
                    return;
                  }
                  changeModelType(value);
                }}
                value={getSelectedModelType}
                placement="topRight"
                options={getModelOptions(userType, getSelectedModelType)}
              />

              {plugins.CNEvaluator === true && (
                <CNEvaluator
                  section={pluginHandlers.CNEvaluator.CNEvaluator_section}
                  setSection={pluginHandlers.CNEvaluator.CNEvaluator_setSection}
                  subsection={pluginHandlers.CNEvaluator.CNEvaluator_subsection}
                  setSubsection={
                    pluginHandlers.CNEvaluator.CNEvaluator_setSubsection
                  }
                  setBlockSubmission={
                    pluginHandlers.CNEvaluator.setBlockSubmission
                  }
                />
              )}
            </div>
          </div>
          <div className={style.bottom} style={{ position: "relative" }}>
            {isUrlBoxOpen ? (
              <div
                className={`${style.urlBox} ${url ? style.urlBoxExtended : ""}`}
              >
                <div className={style.urlBoxInput}>
                  <Input
                    style={{
                      border: "none",
                      resize: "none",
                      maxHeight: "50px",
                      minHeight: "40px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        setIsUrlBoxOpen(false);
                      }
                    }}
                    placeholder="Paste your link here"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value);
                    }}
                    className={classNames(
                      style.textarea,
                      style.textareaFont,
                      // isSubmitting && style.textareaWithoutItalicStyle,
                    )}
                  />
                  <img
                    src={Close2}
                    style={{
                      cursor: "pointer",
                      width: "12px",
                      height: "12px",
                    }}
                    alt="Close URL box."
                    onClick={() => setIsUrlBoxOpen(false)}
                  />
                </div>
                <div className={style.urlBoxBottom}>
                  <span
                    className={style.urlBoxBottomClearButton}
                    onClick={() => setUrl("")}
                  >
                    Clear
                  </span>
                </div>
              </div>
            ) : (
              ""
            )}
            <Form.Item
              name={"q"}
              rules={[
                { required: required, message: "This field is required" },
              ]}
            >
              <div
                style={{
                  border: "1px solid #E6E6E6",
                  borderRadius: "5px",
                  background: "#fff",
                  boxShadow: "0px 1px 4px 1px #0000000F",
                  display: "flex",
                  alignItems: "end",
                  paddingTop:
                    preview || selectedDocuments?.length > 0
                      ? "40px"
                      : undefined,
                }}
              >
                <TextArea
                  style={{ border: "0px", resize: "none" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      onClickEnter(e);
                    }
                  }}
                  placeholder={inputPlaceholder}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                  }}
                  disabled={disabled}
                  autoSize
                  className={classNames(
                    style.textarea,
                    style.textareaFont,
                    // isSubmitting && style.textareaWithoutItalicStyle,
                  )}
                />
                {preview && (
                  <div className={style.previewWrapper}>
                    <div>
                      <img src={preview} alt="preview" />
                      <div
                        className={style.clearFile}
                        onClick={() => {
                          setSelectedImage(null);
                          setPreview("");
                        }}
                      >
                        <img src={Close} alt="Clear file" />
                      </div>
                    </div>
                  </div>
                )}
                {selectedDocuments?.length > 0 && (
                  <div className={style.previewWrapper}>
                    Selected File{selectedDocuments?.length > 1 ? "s" : ""}:{" "}
                    {selectedDocuments.map((e) => (
                      <div style={{ display: "flex", whiteSpace: "nowrap" }}>
                        {e.filename}
                        {plugins.CNEvaluator === true &&
                        pluginHandlers?.CNEvaluator?.conceptNoteUploaded ===
                          true ? null : (
                          <div
                            key={e.id}
                            className={style.clearFile}
                            style={{ position: "inherit" }}
                            onClick={() => {
                              setSelectedDocuments(
                                selectedDocuments.filter(
                                  (el) => el.id !== e.id,
                                ),
                              );
                            }}
                          >
                            <img src={Close} alt="Clear file" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className={style.textareaIcons}
                  style={{ position: "relative" }}
                >
                  {plugins.CNEvaluator === true ? (
                    <CNEvaluatorTooltip3 />
                  ) : null}
                  <div
                    ref={addMenuRef}
                    className={`${style.textareaIconsAdd} ${isAddMenuOpen === true ? style.textareaIconsAddOpen : ""}`}
                    style={
                      isSubmitting
                        ? { display: "none", pointerEvents: "none" }
                        : {}
                    }
                  >
                    <img
                      className={style.icon}
                      onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
                      src={IconAdd}
                    />
                    <div
                      className={style.content}
                      style={
                        plugins.CNEvaluator === true ? { width: "230px" } : {}
                      }
                    >
                      <Upload
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        multiple={plugins.CNEvaluator !== true}
                        fileList={[]}
                        itemRender={() => null}
                        disabled={
                          pluginHandlers.CNEvaluator?.conceptNoteUploaded ===
                          true
                        }
                      >
                        <div className={style.contentUploadWrapper}>
                          <img
                            src={UploadDocument}
                            alt="upload document"
                            className={
                              pluginHandlers.CNEvaluator
                                ?.conceptNoteUploaded === true
                                ? style.disabledBlackSvg
                                : undefined
                            }
                          />
                          <div className={style.description}>
                            {plugins.CNEvaluator === true
                              ? "Upload a Concept Note"
                              : "Upload files"}
                            <br />
                            <span className={style.subDescription}>
                              Only .doc and pdfs
                            </span>
                          </div>
                        </div>
                      </Upload>
                    </div>
                  </div>
                  <div
                    className={style.textareaIcon}
                    onClick={() => {
                      if (isSubmitting) {
                        setIsAddMenuOpen(false);
                        stopGeneration();
                      } else {
                        setIsAddMenuOpen(false);
                        handleSubmit();
                      }
                    }}
                    style={{
                      backgroundColor: isSubmitting ? "transparent" : "",
                      height: isSubmitting ? "35px" : undefined,
                      width: isSubmitting ? "35px" : undefined,
                    }}
                  >
                    {isSubmitting ? (
                      <div
                        style={{ width: "35px", height: "35px" }}
                        onClick={() => stopGeneration()}
                      >
                        <StopGenerationIcon />
                      </div>
                    ) : (
                      <ArrowIconWhite />
                    )}
                  </div>
                </div>
              </div>
            </Form.Item>
            {/* TODO: disabled until further notice */}
            {/*{isFormPage && (*/}
            {/*  <p style={{ marginTop: "15px", fontStyle: "italic" }}>*/}
            {/*    Maximum {maxLength} words*/}
            {/*  </p>*/}
            {/*)}*/}
            {plugins.CNEvaluator === true ? null : (
              <>
                <div className={style.bottomText}>
                  Climate Finance Playground can make mistakes. Check important
                  information.
                </div>
                <div className={style.bottomSubtext}>
                  In-house LLM built with Meta Llama 3
                </div>
              </>
            )}
          </div>
        </Form>
      </div>
    </>
  );
};

//export default memo(PlaygroundForm);
export default PlaygroundForm;
